import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useTranslation = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useTranslation must be used within a LanguageProvider');
  }
  return context;
};

const translations = {
  en: {
    welcome: {
      headline: 'Discover Your Personality Type',
      description: 'Take the scientifically-backed MBTI test and uncover your unique traits, strengths, and potential.',
      features: {
        time: '5 Minutes',
        bilingual: 'Bilingual',
        types: '16 Types'
      },
      startButton: 'Start Quiz',
      privacy: 'Takes only 5 minutes • No signup required'
    },
    quiz: {
      progress: 'Question {current} of {total}',
      next: 'Next',
      previous: 'Previous',
      finish: 'Finish Quiz',
      loading: 'Calculating your results...'
    },
    results: {
      yourType: 'Your Personality Type',
      shareResults: 'Share Your Results',
      downloadCard: 'Download Result Card',
      takeAgain: 'Take Quiz Again',
      traits: 'Key Traits',
      strengths: 'Strengths',
      famousPeople: 'Famous People with This Type',
      description: 'Description'
    }
  },
  ur: {
    welcome: {
      headline: 'اپنی شخصیت کی قسم دریافت کریں',
      description: 'سائنسی طور پر ثابت شدہ MBTI ٹیسٹ لیں اور اپنی منفرد خصوصیات، طاقتیں اور صلاحیتیں دریافت کریں۔',
      features: {
        time: '5 منٹ',
        bilingual: 'دو لسانی',
        types: '16 اقسام'
      },
      startButton: 'کوئز شروع کریں',
      privacy: 'صرف 5 منٹ لگتے ہیں • کوئی رجسٹریشن نہیں'
    },
    quiz: {
      progress: 'سوال {current} کل {total} میں سے',
      next: 'اگلا',
      previous: 'پچھلا',
      finish: 'کوئز مکمل کریں',
      loading: 'آپ کے نتائج کا حساب لگ رہا ہے...'
    },
    results: {
      yourType: 'آپ کی شخصیت کی قسم',
      shareResults: 'اپنے نتائج شیئر کریں',
      downloadCard: 'نتیجہ کارڈ ڈاؤن لوڈ کریں',
      takeAgain: 'دوبارہ کوئز لیں',
      traits: 'اہم خصوصیات',
      strengths: 'طاقتیں',
      famousPeople: 'اس قسم کے مشہور لوگ',
      description: 'تفصیل'
    }
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');

  // Load saved language preference
  useEffect(() => {
    const savedLanguage = localStorage.getItem('mbti-quiz-language');
    if (savedLanguage && translations[savedLanguage]) {
      setLanguage(savedLanguage);
    }
  }, []);

  // Save language preference
  useEffect(() => {
    localStorage.setItem('mbti-quiz-language', language);
  }, [language]);

  const toggleLanguage = (newLanguage) => {
    if (translations[newLanguage]) {
      setLanguage(newLanguage);
    }
  };

  const t = (key, params = {}) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      value = value?.[k];
    }
    
    if (!value) {
      // Fallback to English if translation not found
      value = translations.en;
      for (const k of keys) {
        value = value?.[k];
      }
    }
    
    // Replace parameters in translation
    if (typeof value === 'string' && params) {
      return value.replace(/\{(\w+)\}/g, (match, param) => params[param] || match);
    }
    
    return value || key;
  };

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};
